import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { InstallationItemGatewayDeviceFull } from '../../../projects/client';
import { ServiceTicketActions } from './service-ticket.actions';
import { ServiceTicketSelectors } from './service-ticket.selectors';
import {
  ServiceTicketArrangedCallback,
  ServiceTicketCallNowDetails,
  ServiceTicketCallType,
} from './service-ticket.model';

@Injectable({ providedIn: 'root' })
export class ServiceTicketStore {
  activePage$ = this.store.select(ServiceTicketSelectors.selectActivePage);
  createPageAvailableSteps$ = this.store.select(
    ServiceTicketSelectors.selectCreatePageAvailableSteps
  );
  callPageAvailableSteps$ = this.store.select(ServiceTicketSelectors.selectCallPageAvailableSteps);
  searchedBoilerSerial$ = this.store.select(ServiceTicketSelectors.selectSearchedBoilerSerial);
  displayBoilerSerialError$ = this.store.select(
    ServiceTicketSelectors.selectDisplayBoilerSerialError
  );
  displayBypassValidationCheckbox$ = this.store.select(
    ServiceTicketSelectors.selectDisplayBypassValidationCheckbox
  );
  boilerDeviceErrorDetails$ = this.store.select(
    ServiceTicketSelectors.selectBoilerDeviceErrorDetails
  );
  allBoilerDeviceErrorDetails$ = this.store.select(
    ServiceTicketSelectors.selectAllBoilerDeviceErrorDetails
  );
  canRequestCallNow$ = this.store.select(ServiceTicketSelectors.selectCanRequestCallNow);
  canRequestArrangedCallback$ = this.store.select(
    ServiceTicketSelectors.selectCanRequestArrangedCallback
  );
  errorTicketDetails$ = this.store.select(ServiceTicketSelectors.selectErrorTicketDetails);
  callTypeInteractionDisabled$ = this.store.select(
    ServiceTicketSelectors.selectCallTypeInteractionDisabled
  );
  callNowDetails$: Observable<ServiceTicketCallNowDetails> = this.store.select(
    ServiceTicketSelectors.selectCallNowDetails
  );
  arrangedCallbackDetails$: Observable<ServiceTicketArrangedCallback> = this.store.select(
    ServiceTicketSelectors.selectArrangedCallbackDetails
  );
  arrangedCallbacks$: Observable<ServiceTicketArrangedCallback[]> = this.store.select(
    ServiceTicketSelectors.selectArrangedCallbacks
  );
  assistantArticlesSummary$ = this.store.select(
    ServiceTicketSelectors.selectAssistantArticlesSummary
  );
  displayArticleDetails$ = this.store.select(ServiceTicketSelectors.selectDisplayArticleDetails);
  articleFeedbackDetails$ = this.store.select(ServiceTicketSelectors.selectArticleFeedbackDetails);
  errorCodes$ = this.store.select(ServiceTicketSelectors.selectErrorCodes);
  isAllowedToUseCallNowByVkorg$ = this.store.select(
    ServiceTicketSelectors.selectIsAllowedToUseCallNowByVkorg
  );
  routingProvider$ = this.store.select(ServiceTicketSelectors.selectRoutingProvider);
  boilerSerial$ = this.store.select(ServiceTicketSelectors.selectSearchSerialNumber);
  isRoutingProviderError$ = this.store.select(ServiceTicketSelectors.selectIsRoutingProviderError);
  isSalesforceWithGermanyCompany$ = this.store.select(
    ServiceTicketSelectors.selectIsSalesforceWithGermanyCompany
  );
  requestRoutingProviderPayloadBoilerSerial$ = this.store.select(
    ServiceTicketSelectors.selectRequestRoutingProviderPayloadBoilerSerial
  );
  boilerSerialValidationError$ = this.store.select(ServiceTicketSelectors.selectBoilerSerialError);

  constructor(private store: Store) {}

  // General

  openCallPage(): void {
    this.store.dispatch(ServiceTicketActions.navigation.openCallPage());
  }

  openArticlesPage(): void {
    this.store.dispatch(ServiceTicketActions.navigation.openArticlesPage());
  }

  navigateBackToCreatePage(): void {
    this.store.dispatch(ServiceTicketActions.navigation.navigateBackToCreatePage());
  }

  closeServiceTicket(): void {
    this.store.dispatch(ServiceTicketActions.navigation.close());
  }

  loadCachedCallbacks(): void {
    this.store.dispatch(ServiceTicketActions.loadCachedCallbacks.attempt());
  }

  removeCachedCallback(id: string): void {
    this.store.dispatch(ServiceTicketActions.removeCachedCallback.attempt({ id }));
  }

  // Create Page

  updateSearchedBoilerSerial(boilerSerial: string): void {
    this.store.dispatch(ServiceTicketActions.updateSearchedBoilerSerial.attempt({ boilerSerial }));
  }

  bypassBoilerSerialValidation(): void {
    this.store.dispatch(ServiceTicketActions.bypassBoilerSerialValidation.attempt());
  }

  updateNoErrorCodeSelection(isNoErrorCode: boolean): void {
    this.store.dispatch(ServiceTicketActions.updateNoErrorCodeSelection.attempt({ isNoErrorCode }));
  }

  updateSelectedErrorCodes(errorCodes: string[], otherErrorSelected: boolean): void {
    this.store.dispatch(
      ServiceTicketActions.updateSelectedErrorCodes.attempt({ errorCodes, otherErrorSelected })
    );
  }

  updateOtherErrorCode(otherErrorCode: string): void {
    this.store.dispatch(
      ServiceTicketActions.updateOtherErrorCode.loadDocuments({ otherErrorCode })
    );
  }

  boilerSerialAutocompleteDevices(
    installationId?: string
  ): Observable<InstallationItemGatewayDeviceFull[]> {
    return this.store.select(
      ServiceTicketSelectors.selectBoilerSerialAutocompleteDevices(installationId)
    );
  }

  // Call Page

  updateFeedback(feedback: string): void {
    this.store.dispatch(ServiceTicketActions.updateFeedback.attempt({ feedback }));
  }

  updatePhoneNumber(phoneNumber: string, isValidPhoneNumber: boolean): void {
    this.store.dispatch(
      ServiceTicketActions.updatePhoneNumber.attempt({ phoneNumber, isValidPhoneNumber })
    );
  }

  updateCallType(callType: ServiceTicketCallType): void {
    this.store.dispatch(ServiceTicketActions.updateCallType.attempt({ callType }));
  }

  requestCallNow(): void {
    this.store.dispatch(
      ServiceTicketActions.requestWaitingTime.attempt({ callType: ServiceTicketCallType.CallNow })
    );
  }

  requestRoutingProvider(boilerSerial: string): void {
    this.store.dispatch(ServiceTicketActions.routingProviderRequest.attempt({ boilerSerial }));
  }

  callNowTimeExpired(): void {
    this.store.dispatch(ServiceTicketActions.callNowTimeExpired.attempt());
  }

  requestArrangedCall(): void {
    this.store.dispatch(
      ServiceTicketActions.requestWaitingTime.attempt({
        callType: ServiceTicketCallType.ArrangedCallback,
      })
    );
  }

  // Articles page

  setActiveArticle(): void {
    this.store.dispatch(ServiceTicketActions.callNowTimeExpired.attempt());
  }

  rateSelectedArticle(isPositive: boolean): void {
    this.store.dispatch(ServiceTicketActions.rateSelectedArticle.attempt({ isPositive }));
  }
}
